import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ValetApi from "../api/Api";
import { toast } from "react-toastify";
import { FormGroup, Label, Input, Button, Alert } from "reactstrap";

const validationSchema = Yup.object({
    region_id: Yup.string().required("Region required"),
    location_id: Yup.string().required("Location required"),
    podium_ids: Yup.array().min(1, "Select at least one podium"),
    email: Yup.string().email("Invalid email").required("Email required"),
});

const GenerateInviteForm = () => {
    const [regions, setRegions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [podiums, setPodiums] = useState([]);
    const [inviteCode, setInviteCode] = useState("");
    const [inviteEmailConfig, setInviteEmailConfig] = useState(null);
    const [inviteConfigLoading, setInviteConfigLoading] = useState(false);
    const [inviteConfigError, setInviteConfigError] = useState(false);

    async function loadInviteConfig() {
        setInviteConfigLoading(true);
        setInviteConfigError(false);
        try {
            const res = await ValetApi.getInviteConfig();
            setInviteEmailConfig(res.data?.inviteEmail || null);
        } catch {
            setInviteEmailConfig(null);
            setInviteConfigError(true);
        } finally {
            setInviteConfigLoading(false);
        }
    }

    useEffect(() => {
        async function fetchRegions() {
            const res = await ValetApi.getAllRegions();
            setRegions(res.data.regions || []);
        }
        fetchRegions();
    }, []);

    useEffect(() => {
        loadInviteConfig();
    }, []);

    function renderInviteEmailIndicator() {
        if (!inviteEmailConfig && !inviteConfigError && !inviteConfigLoading) return null;

        if (!inviteEmailConfig) {
            return (
                <Alert
                    color={inviteConfigError ? "secondary" : "light"}
                    fade={false}
                    className="py-2 px-3 d-flex justify-content-between align-items-center gap-2">
                    <div>
                        <strong>Invite Email Status</strong>
                        <div>{inviteConfigError ? "Unable to load backend invite email mode." : "Loading invite email mode..."}</div>
                    </div>
                    <Button
                        color="secondary"
                        size="sm"
                        type="button"
                        onClick={loadInviteConfig}
                        disabled={inviteConfigLoading}>
                        {inviteConfigLoading ? "Refreshing..." : "Refresh Status"}
                    </Button>
                </Alert>
            );
        }

        const { enabled, safeMode, allowlistCount } = inviteEmailConfig;
        let color = "secondary";
        let title = "Invite Email: Unknown";
        let detail = "Could not determine current backend invite email mode.";

        if (!enabled) {
            color = "warning";
            title = "Invite Email: Off";
            detail = "Emails are disabled. Invite codes will be generated without sending email.";
        } else if (safeMode) {
            color = "info";
            title = "Invite Email: Safe Mode";
            detail = `Emails are enabled with allowlist restrictions (${allowlistCount} allowed address${allowlistCount === 1 ? "" : "es"}).`;
        } else {
            color = "success";
            title = "Invite Email: On";
            detail = "Emails are enabled and not restricted by allowlist.";
        }

        return (
            <Alert
                color={color}
                fade={false}
                className="py-2 px-3 d-flex justify-content-between align-items-center gap-2">
                <div>
                    <strong>{title}</strong>
                    <div>{detail}</div>
                </div>
                <Button
                    color="secondary"
                    size="sm"
                    type="button"
                    onClick={loadInviteConfig}
                    disabled={inviteConfigLoading}>
                    {inviteConfigLoading ? "Refreshing..." : "Refresh Status"}
                </Button>
            </Alert>
        );
    }

    const handleRegionChange = async (region_id, setFieldValue) => {
        setFieldValue("location_id", "");
        setFieldValue("podium_ids", []);
        setLocations([]);
        setPodiums([]);
        if (region_id) {
            const res = await ValetApi.getLocationsByRegion(region_id);
            setLocations(res.data.locations || []);
        }
    };

    const handleLocationChange = async (location_id, setFieldValue) => {
        setFieldValue("podium_ids", []);
        setPodiums([]);
        if (location_id) {
            const res = await ValetApi.getPodiumsByLocation(location_id);
            setPodiums(res.data.podiums || []);
        }
    };

    return (
        <Formik
            initialValues={{
                region_id: "",
                location_id: "",
                podium_ids: [],
                email: "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
                try {
                    const payload = {
                        ...values,
                        podium_ids: (values.podium_ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id)),
                    };

                    const res = await ValetApi.generateInvite(payload);
                    setInviteCode(res.data.invite.code);
                    const emailDelivery = res.data?.emailDelivery;
                    if (emailDelivery?.skipped) {
                        toast.success(`Invite generated. Email not sent (${emailDelivery.reason}).`);
                    } else {
                        toast.success("Invite generated and emailed!");
                    }
                    resetForm();
                } catch {
                    toast.error("Error generating invite.");
                } finally {
                    setSubmitting(false);
                }
            }}>
            {({ values, setFieldValue, isSubmitting }) => {
                const selectedPodiumIds = (values.podium_ids || []).map((id) => String(id));

                const setPodiumChecked = (podiumId, checked) => {
                    const nextId = String(podiumId);
                    const nextPodiumIds = checked ? Array.from(new Set([...selectedPodiumIds, nextId])) : selectedPodiumIds.filter((pid) => pid !== nextId);

                    setFieldValue("podium_ids", nextPodiumIds, true);
                };

                return (
                    <Form className="invite-form">
                        {renderInviteEmailIndicator()}
                        <FormGroup>
                            <Label for="region_id">Region</Label>
                            <Input
                                tag={Field}
                                as="select"
                                name="region_id"
                                id="region_id"
                                onChange={async (e) => {
                                    setFieldValue("region_id", e.target.value);
                                    await handleRegionChange(e.target.value, setFieldValue);
                                }}
                                value={values.region_id}>
                                <option value="">Select Region</option>
                                {regions.map((r) => (
                                    <option
                                        key={r.id}
                                        value={r.id}>
                                        {r.name}
                                    </option>
                                ))}
                            </Input>
                            <ErrorMessage
                                name="region_id"
                                component="div"
                                className="text-danger"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="location_id">Location</Label>
                            <Input
                                tag={Field}
                                as="select"
                                name="location_id"
                                id="location_id"
                                onChange={async (e) => {
                                    setFieldValue("location_id", e.target.value);
                                    await handleLocationChange(e.target.value, setFieldValue);
                                }}
                                value={values.location_id}
                                disabled={!values.region_id}>
                                <option value="">Select Location</option>
                                {locations.map((l) => (
                                    <option
                                        key={l.id}
                                        value={l.id}>
                                        {l.name}
                                    </option>
                                ))}
                            </Input>
                            <ErrorMessage
                                name="location_id"
                                component="div"
                                className="text-danger"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Podiums</Label>
                            <div className="invite-podium-list">
                                {podiums.map((p) => (
                                    <div
                                        key={p.id}
                                        className="invite-podium-option">
                                        <input
                                            type="checkbox"
                                            className="invite-podium-checkbox"
                                            aria-label={p.name}
                                            checked={selectedPodiumIds.includes(String(p.id))}
                                            onChange={(evt) => setPodiumChecked(p.id, evt.currentTarget.checked)}
                                            id={`podium-${p.id}`}
                                        />
                                        <label
                                            htmlFor={`podium-${p.id}`}
                                            className="invite-podium-label">
                                            {p.name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <ErrorMessage
                                name="podium_ids"
                                component="div"
                                className="text-danger"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="email">User Email</Label>
                            <Input
                                tag={Field}
                                name="email"
                                id="email"
                                type="email"
                            />
                            <ErrorMessage
                                name="email"
                                component="div"
                                className="text-danger"
                            />
                        </FormGroup>
                        <Button
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}>
                            Generate Invite
                        </Button>
                        {inviteCode && (
                            <div className="invite-result">
                                <h5>Invite Code</h5>
                                <div>
                                    <b>{inviteCode}</b>
                                </div>
                            </div>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default GenerateInviteForm;
